import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import SearchSelect from '../../components/common/SearchSelect';
import LookupSelect from '../../components/common/LookupSelect';
import { useNotifications } from '../../context/NotificationContext';
import { dsPackageApi } from '../../services/api';

/**
 * Create a Submittal Package (the ISA datasheet package). Two steps on purpose:
 *
 *   1. Pick the source and exec type. NOTHING is written here. The package number, first
 *      revision, revision date, status, remarks and drafter are all set by the server on
 *      create, so the form does not show them.
 *   2. Pre-flight — counts only: how many tags matched, how many can produce a datasheet, how
 *      many cannot, and the unique index sheets. No per-tag list: on a real project most index
 *      sheets have no template assigned at first, and the list was noise at that scale.
 *      Generating with skips is allowed and expected.
 *
 * Self-contained state with an inline error, following NewDsModal in DSComposerPage rather than
 * the page-level pattern — every failure here is about this form, so it belongs in the form.
 */

export default function DsPackageCreateModal({ onClose, onCreated, auditCfg }) {
  // Errors go to the AlertBar, not a loose div: the .stui-alert-* classes are modifiers of
  // .stui-alert-item inside that bar and have no padding or shape of their own.
  const { notify } = useNotifications();
  const [step, setStep] = useState('form');
  const [sources, setSources] = useState([]);
  const [sourceId, setSourceId] = useState(null);
  const [execType, setExecType] = useState('');
  const [desc, setDesc] = useState('');
  const [preflight, setPreflight] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    dsPackageApi.sources()
      .then(res => setSources(res.data || []))
      .catch(err => notify.error(err?.response?.data?.error || 'Failed to load sources.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sourceOptions = useMemo(
    () => sources.map(s => ({
      id: s.id,
      display: `${s.projectNumber} — ${s.projectDesc || ''} (${s.tagCount} tags)`,
    })),
    [sources]
  );

  const selectedSource = sources.find(s => s.id === sourceId) || null;

  const runPreflight = async () => {
    if (!sourceId) { notify.error('Pick a source.'); return; }
    if (!execType) { notify.error('Pick an exec type.'); return; }

    setBusy(true);
    try {
      const res = await dsPackageApi.preflight({ sourceId, execType });
      setPreflight(res.data);
      setStep('preflight');
    } catch (err) {
      notify.error(err?.response?.data?.error || 'Pre-flight failed.');
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const res = await dsPackageApi.create(
        { sourceId, execType, dsPackageDesc: desc }, auditCfg
      );
      onCreated?.(res.data);
      onClose?.();
    } catch (err) {
      notify.error(err?.response?.data?.error || 'Generation failed.');
      setBusy(false);
    }
  };

  const eligible = preflight?.withTemplate ?? 0;

  return (
    <div className="stui-modal-overlay" onClick={onClose}>
      <div className="stui-modal stui-modal-lg" onClick={e => e.stopPropagation()}>
        <div className="stui-modal-header">
          <span className="stui-modal-title">New Submittal Package</span>
          <button className="stui-fk-select-btn" onClick={onClose} title="Close"><X size={20} /></button>
        </div>

        <div className="stui-modal-body">
          {step === 'form' && (
            <div className="stui-detail-grid">
              <div className="stui-field-group stui-col-24">
                <label>Source</label>
                <SearchSelect
                  value={sourceId}
                  displayValue={selectedSource
                    ? `${selectedSource.projectNumber} — ${selectedSource.projectDesc || ''}`
                    : ''}
                  options={sourceOptions}
                  onChange={(v) => setSourceId(v ? Number(v) : null)}
                  placeholder="-- Select a source --"
                />
              </div>

              <div className="stui-field-group stui-col-12">
                <label>Exec Type</label>
                {/* DS_ExecType is the one lookup this feature uses; the two status sets are
                    code-owned because a value added in the UI would fail a CHECK constraint. */}
                <LookupSelect
                  category="DS_ExecType"
                  value={execType}
                  onChange={setExecType}
                  className="stui-select"
                />
              </div>

              <div className="stui-field-group stui-col-24">
                <label>Description</label>
                <input
                  className="stui-field-input"
                  value={desc}
                  onChange={e => setDesc(e.target.value)}
                  placeholder="What this package covers"
                  maxLength={500}
                />
              </div>
            </div>
          )}

          {step === 'preflight' && preflight && (
            <>
              <div className="stui-detail-grid">
                <div className="stui-field-group stui-col-8">
                  <label>Tags Matched</label>
                  <div className="stui-field-readonly">{preflight.matched}</div>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Will Generate</label>
                  <div className="stui-field-readonly">{preflight.withTemplate}</div>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Cannot Generate</label>
                  <div className="stui-field-readonly">{preflight.withoutTemplate}</div>
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Datasheets (unique index sheets)</label>
                  <div className="stui-field-readonly">{preflight.indexGroupCount}</div>
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Tags With No Source At All</label>
                  <div className="stui-field-readonly">{preflight.noSourceTagCount}</div>
                </div>
              </div>

              {preflight.withoutTemplate > 0 && (
                <>
                  <p className="stui-hint">
                    {preflight.withoutTemplate} of {preflight.matched} tags cannot produce a
                    datasheet yet and will be left out. Nothing is lost — assign a template and
                    issue a revision to pick them up.
                  </p>
                </>
              )}

              {eligible === 0 && (
                <p className="stui-hint">
                  Nothing to generate. Assign a datasheet template to at least one index sheet
                  first.
                </p>
              )}
            </>
          )}
        </div>

        <div className="stui-modal-footer">
          {step === 'form' ? (
            <>
              <button className="stui-btn stui-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={runPreflight} disabled={busy || !sourceId || !execType}>
                {busy ? 'Checking…' : 'Next'}
              </button>
            </>
          ) : (
            <>
              <button className="stui-btn stui-btn-secondary" onClick={() => setStep('form')} disabled={busy}>Back</button>
              <button className="stui-btn stui-btn-primary" onClick={generate} disabled={busy || eligible === 0}>
                {busy ? 'Generating…' : `Generate ${eligible} Record${eligible === 1 ? '' : 's'}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
